import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { useFormik, FormikValues } from "formik";
import { HexColorPicker } from "react-colorful";
import {
  useCreateDarkThemeColorOrderMutation,
  useCreateLightThemeColorOderMutation,
  useGetAllDarkThemeColorQuery,
  useGetAllLightThemeColorQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { IAuth } from "../../../Api/Interface/api.interface";
import { useThemeContext } from "../../../Drawer/Menu/ThemeContext";
import useIsMobile from "../../../Core/hook/useIsMobile";

interface ILightTheme {
  id: string;
  orderListProp: ILightThemeEx;
}

interface ILightThemeEx {
  id: string;
  leftBgColor: string;
  leftTopBgColor: string;
  rightBgColor: string;
  rightTopBgColor: string;
  buttonBgColor: string;
  buttonIconColor: string;
  cardBorderRadius: string;
  buttonRadius: string;
  headingColor: string;
  headingFontSize: string;
  productCardBgColor: string;
  borderBgColor: string;
  payButtonColor: string;
  leftFontColor: string;
  rightFontColor: string;


}

interface IDarkTheme {
  id: string;
  orderListProp: IDarkThemeEx;
}
interface IDarkThemeEx {
  id: string;
  leftBgColor: string;
  leftTopBgColor: string;
  rightBgColor: string;
  rightTopBgColor: string;
  buttonBgColor: string;
  buttonIconColor: string;
  cardBorderRadius: string;
  buttonRadius: string;
  headingColor: string;
  headingFontSize: string;
  productCardBgColor: string;
  borderBgColor: string;
  payButtonColor: string;
  leftFontColor: string;
  rightFontColor: string;

}

enum Section {
  Dark = "Dark",
  Light = "Light",
}

interface IColorPopUp {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
}

const ColorPopup = ({ openModel = false, handleCloseDialog }: IColorPopUp) => {
  const classes = useStyles();
  const theme = useTheme();
  const [open] = React.useState(openModel);
  const [activeSection, setActiveSection] = useState<Section>(Section.Light);
  const handleContainerChange = (section: Section) => {
    setActiveSection(section);
  };
  const isMobileRes = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const { mode, toggleTheme } = useThemeContext();

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const { data, isLoading } = useGetAllLightThemeColorQuery({
    searchText: "",
    id: "1",
  });
  const { data: darkData, isLoading: darkIsLoading } =
    useGetAllDarkThemeColorQuery({
      searchText: "",
      id: "1",
    });

  const [settingData] = useCreateLightThemeColorOderMutation();
  const [darkthemeData] = useCreateDarkThemeColorOrderMutation();

  const [initialData, setInitialData] = useState<ILightThemeEx | null>(null);
  const [selectedField, setSelectedField] = useState<string>("");
  const [tempColor, setTempColor] = useState<string>("");
  const [initialDarkData, setInitialDarkData] = useState<IDarkThemeEx | null>(
    null
  );
  const [selectedDarkField, setSelectedDarkField] = useState<string>("");
  const [tempColorDark, setTempColorDark] = useState<string>("");

  useEffect(() => {
    if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
      const themeColor = data.data[0];
      // const themeColorOderderprop = themeColor.orderListProp[0];

      setInitialData({
        id: themeColor.id.toString(),
        cardBorderRadius: themeColor.orderListProp.cardBorderRadius || "none",
        buttonRadius: themeColor.orderListProp.buttonRadius || "4px",
        leftBgColor: themeColor.orderListProp.leftBgColor || "#ffffff",
        leftTopBgColor: themeColor.orderListProp.leftTopBgColor || "#ffffff",
        rightBgColor: themeColor.orderListProp.rightBgColor || "#ffffff",
        rightTopBgColor: themeColor.orderListProp.rightTopBgColor || "#ffffff",
        buttonBgColor: themeColor.orderListProp.buttonBgColor || "#eaf3f2",
        buttonIconColor: themeColor.orderListProp.buttonIconColor || "#599d95",
        productCardBgColor: themeColor.orderListProp.productCardBgColor || "#eaf3f2",
        headingColor: themeColor.orderListProp.headingColor || "#639182",
        headingFontSize: themeColor.orderListProp.headingFontSize || "21px",
        borderBgColor: themeColor.orderListProp.borderBgColor || "#e8f0f3",
        payButtonColor: themeColor.orderListProp.payButtonColor || "#298076",
        leftFontColor: themeColor.orderListProp.leftFontColor || "#85858f",
        rightFontColor: themeColor.orderListProp.rightFontColor || "#9a9da1",
      });
    }
  }, [data]);

  useEffect(() => {
    if (
      darkData?.data &&
      Array.isArray(darkData.data) &&
      darkData.data.length > 0
    ) {
      const themeColor = darkData.data[0];
      setInitialDarkData({
        id: themeColor.id.toString(),
        cardBorderRadius: themeColor.orderListProp.cardBorderRadius || "none",
        buttonRadius: themeColor.orderListProp.buttonRadius || "4px",
        leftBgColor: themeColor.orderListProp.leftBgColor || "#1c2b41",
        leftTopBgColor: themeColor.orderListProp.leftTopBgColor || "#1c2b41",
        rightBgColor: themeColor.orderListProp.rightBgColor || "#1c2b41",
        rightTopBgColor: themeColor.orderListProp.rightTopBgColor || "#1c2b41",
        buttonBgColor: themeColor.orderListProp.buttonBgColor || "#22272b",
        buttonIconColor: themeColor.orderListProp.buttonIconColor || "#b6c2cf",
        headingColor: themeColor.orderListProp.headingColor || "#a3aeba",
        headingFontSize: themeColor.orderListProp.headingFontSize || "21px",
        productCardBgColor: themeColor.orderListProp.productCardBgColor || "#616467",
        borderBgColor: themeColor.orderListProp.borderBgColor || "#9f8fef",
        payButtonColor: themeColor.orderListProp.payButtonColor || "#c55827",
        leftFontColor: themeColor.orderListProp.leftFontColor || "#b3bfcc",
        rightFontColor: themeColor.orderListProp.rightFontColor || "#b3bfcc",

      });
    }
  }, [darkData]);

  const formik = useFormik<ILightThemeEx>({
    initialValues: {
      id: initialData?.id || "",
      cardBorderRadius: initialData?.cardBorderRadius || "none",
      buttonRadius: initialData?.buttonRadius || "4px",
      leftBgColor: initialData?.leftBgColor || "#776c7b",
      leftTopBgColor: initialData?.leftTopBgColor || "#ffffff",
      rightBgColor: initialData?.rightBgColor || "#ffffff",
      rightTopBgColor: initialData?.rightTopBgColor || "#ffffff",
      buttonBgColor: initialData?.buttonBgColor || "#eaf3f2",
      buttonIconColor: initialData?.buttonIconColor || "#599d95",
      headingColor: initialData?.headingColor || "#639182",
      headingFontSize: initialData?.headingFontSize ||  "21px",
      productCardBgColor: initialData?.productCardBgColor || "#eaf3f2",
      borderBgColor: initialData?.borderBgColor || "#e8f0f3",
      payButtonColor: initialData?.payButtonColor || "#298076",
      leftFontColor: initialData?.leftFontColor || "#85858f",
      rightFontColor: initialData?.rightFontColor || "#9a9da1",


    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = initialData
          ? { ...values, id: initialData.id }
          : { ...values };

        const response = await settingData(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          const tokenData = response.data as IAuth;
          if (tokenData) {
            localStorage.setItem("lightThemeColor", JSON.stringify(tokenData));
            window.location.reload();
          }
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formikDark = useFormik<IDarkThemeEx>({
    initialValues: {
      id: initialDarkData?.id || "",
      cardBorderRadius: initialDarkData?.cardBorderRadius || "none",
      buttonRadius: initialDarkData?.buttonRadius || "4px",
      leftBgColor: initialDarkData?.leftBgColor || "#1c2b41",
      leftTopBgColor: initialDarkData?.leftTopBgColor || "#1c2b41",
      rightBgColor: initialDarkData?.rightBgColor || "#1c2b41",
      rightTopBgColor: initialDarkData?.rightTopBgColor || "#1c2b41",
      buttonBgColor: initialDarkData?.buttonBgColor || "#22272b",
      buttonIconColor: initialData?.buttonIconColor || "#b6c2cf",
      headingColor: initialDarkData?.headingColor || "#a3aeba",
      headingFontSize: initialDarkData?.headingFontSize || "21px",
      productCardBgColor: initialDarkData?.productCardBgColor || "#616467",
      borderBgColor: initialDarkData?.borderBgColor || "#9f8fef",
      payButtonColor: initialDarkData?.payButtonColor || "#c55827",
      leftFontColor: initialDarkData?.leftFontColor || "#b3bfcc",
      rightFontColor: initialDarkData?.rightFontColor || "#b3bfcc",

    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = initialDarkData
          ? { ...values, id: initialDarkData.id }
          : { ...values };

        const response = await darkthemeData(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          const tokenData = response.data as IAuth;
          if (tokenData) {
            localStorage.setItem("darkThemeColor", JSON.stringify(tokenData));
            window.location.reload();
          }
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.leftBgColor !== "";
  }, [formik]);

  const formValidDark = useMemo(() => {
    return formikDark.values.leftBgColor !== "";
  }, [formikDark]);

  const openColorPicker = (field: keyof ILightThemeEx) => {
    setTempColor(formik.values[field]);
    setSelectedField(field);
  };

  const openColorPickerDark = (field: keyof IDarkThemeEx) => {
    setTempColorDark(formikDark.values[field]);
    setSelectedDarkField(field);
  };

  const applyColor = () => {
    if (selectedField) {
      formik.setFieldValue(selectedField, tempColor);
    }
    setSelectedField("");
  };

  const applyColorDark = () => {
    if (selectedDarkField) {
      formikDark.setFieldValue(selectedDarkField, tempColorDark);
    }
    setSelectedDarkField("");
  };

  const renderContent = () => {
    switch (activeSection) {
      case Section.Light:
        return (
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {[
                    { label: "Left Background", field: "leftBgColor" },
                    { label: "Left Top Background", field: "leftTopBgColor" },
                    { label: "Right Background", field: "rightBgColor" },
                    { label: "Right Top Background", field: "rightTopBgColor" },
                    { label: "Button Background", field: "buttonBgColor" },
                    { label: "Button Icon", field: "buttonIconColor" },
                    { label: "Heading", field: "headingColor" },
                    { label: "Border", field: "borderBgColor" },
                    {
                      label: "product Card Background",
                      field: "productCardBgColor",
                    },
                    { label: "Pay Button", field: "payButtonColor" },
                    { label: "Left Font Color", field: "leftFontColor" },
                    { label: "Right Font Color", field: "rightFontColor" },


                  ].map(({ label, field }) => (
                    <Grid item lg={3} md={6} sm={6} xs={12} key={field}>
                      <Card className={classes.card}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          p={2}
                        >
                          <Box>
                            <Typography variant="subtitle1">{label}</Typography>
                            <Box display="flex" alignItems="center" mt={1}>
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  backgroundColor:
                                    formik.values[field as keyof ILightThemeEx],
                                  border: "1px solid #d3d3d3",
                                  borderRadius: "8px",
                                  marginRight: 2,
                                }}
                              />
                              {/* <Typography variant="body1">
                          {formik.values[field as keyof ILightTheme]}
                        </Typography> */}
                              <TextField
                                size="small"
                                variant="standard"
                                InputProps={{
                                  disableUnderline: true,
                                  sx: {
                                    fontSize: 14,
                                    border: "none",
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                value={
                                  formik.values[field as keyof IDarkThemeEx]
                                }
                                onChange={(e) =>
                                  formik.setFieldValue(field, e.target.value)
                                }
                              />
                            </Box>
                          </Box>
                          <IconButton
                            color="primary"
                            onClick={() =>
                              openColorPicker(field as keyof IDarkThemeEx)
                            }
                          >
                            <ColorLensIcon sx={{ fontSize: 40 }} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}

                  <Grid item lg={3} md={6} sm={6} xs={12}>
                    <Card className={classes.card}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            Card Border Radius
                          </Typography>
                          <Box display="flex" alignItems="center" mt={1} pb={1}>
                            <TextField
                              placeholder="Card Border Radius"
                              size="small"
                              {...formik.getFieldProps("cardBorderRadius")}
                              // type="number"
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item lg={3} md={6} sm={6} xs={12}>
                    <Card className={classes.card}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            Button Border Radius
                          </Typography>
                          <Box display="flex" alignItems="center" mt={1} pb={1}>
                            <TextField
                              placeholder="Button Border Radius"
                              size="small"
                              {...formik.getFieldProps("buttonRadius")}
                              // type="number"
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item lg={3} md={6} sm={6} xs={12}>
                    <Card className={classes.card}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={2}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            Heading Font Size
                          </Typography>
                          <Box display="flex" alignItems="center" mt={1} pb={1}>
                            <TextField
                              placeholder="Heading Font Size"
                              size="small"
                              {...formik.getFieldProps("headingFontSize")}
                              // type="number"
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>
                          <Button
                            variant="contained"
                            startIcon={<SaveAltIcon />}
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid}
                          >
                            Update
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {selectedField && (
              <Dialog
                open={!!selectedField}
                onClose={() => setSelectedField("")}
              >
                <Grid className={isMobileRes ? classes.DialogMobile :classes.Dialog}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Set Color
                      </Typography>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px",
                      }}
                    >
                      <HexColorPicker
                        color={tempColor}
                        onChange={setTempColor}
                        style={{
                          width: "250px",
                          height: "250px",
                        }}
                      />
                    </Box>
                  </DialogContent>

                  <DialogActions
                    sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}
                  >
                    <Grid container spacing={3}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={() => setSelectedField("")}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<ColorLensIcon />}
                          className={classes.addButton}
                          onClick={applyColor}
                        >
                          Apply Color
                        </Button>
                      </Grid>
                    </Grid>
                  </DialogActions>
                </Grid>
              </Dialog>
            )}
          </Grid>
        );
      case Section.Dark:
        return (
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <form onSubmit={formikDark.handleSubmit}>
                <Grid container spacing={3}>
                  {[
                    { label: "Left Background", field: "leftBgColor" },
                    { label: "Left Top Background", field: "leftTopBgColor" },
                    { label: "Right Background", field: "rightBgColor" },
                    { label: "Right Top Background", field: "rightTopBgColor" },
                    { label: "Button Background", field: "buttonBgColor" },
                    { label: "Button Icon", field: "buttonIconColor" },
                    { label: "Heading", field: "headingColor" },
                    { label: "Border", field: "borderBgColor" },
                    {
                      label: "product Card Background",
                      field: "productCardBgColor",
                    },
                    { label: "Pay Button", field: "payButtonColor" },
                    { label: "Left Font Color", field: "leftFontColor" },
                    { label: "Right Font Color", field: "rightFontColor" },
                  ].map(({ label, field }) => (
                    <Grid item lg={3} md={6} sm={6} xs={12} key={field}>
                      <Card className={classes.card}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          p={2}
                        >
                          <Box>
                            <Typography variant="subtitle1">{label}</Typography>
                            <Box display="flex" alignItems="center" mt={1}>
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  backgroundColor:
                                    formikDark.values[
                                      field as keyof IDarkThemeEx
                                    ],
                                  border: "1px solid #d3d3d3",
                                  borderRadius: "8px",
                                  marginRight: 2,
                                }}
                              />
                              {/* <Typography variant="body1">
                          {formik.values[field as keyof ILightTheme]}
                        </Typography> */}
                              <TextField
                                size="small"
                                variant="standard"
                                InputProps={{
                                  disableUnderline: true,
                                  sx: {
                                    fontSize: 14,
                                    border: "none",
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                value={
                                  formikDark.values[field as keyof IDarkThemeEx]
                                }
                                onChange={(e) =>
                                  formikDark.setFieldValue(
                                    field,
                                    e.target.value
                                  )
                                }
                              />
                            </Box>
                          </Box>
                          <IconButton
                            color="primary"
                            onClick={() =>
                              openColorPickerDark(field as keyof IDarkThemeEx)
                            }
                          >
                            <ColorLensIcon sx={{ fontSize: 40 }} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>
                          <Button
                            variant="contained"
                            startIcon={<SaveAltIcon />}
                            className={classes.addButton}
                            onClick={() => formikDark.handleSubmit()}
                            disabled={!formValidDark}
                          >
                            Update
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {selectedDarkField && (
              <Dialog
                open={!!selectedDarkField}
                onClose={() => setSelectedDarkField("")}
              >
                <Grid className={isMobileRes ? classes.DialogMobile :classes.Dialog}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Set Color
                      </Typography>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px",
                      }}
                    >
                      <HexColorPicker
                        color={tempColorDark}
                        onChange={setTempColorDark}
                        style={{
                          width: "250px",
                          height: "250px",
                        }}
                      />
                    </Box>
                  </DialogContent>

                  <DialogActions
                    sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}
                  >
                    <Grid container spacing={3}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={() => setSelectedDarkField("")}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<ColorLensIcon />}
                          className={classes.addButton}
                          onClick={applyColorDark}
                        >
                          Apply Color
                        </Button>
                      </Grid>
                    </Grid>
                  </DialogActions>
                </Grid>
              </Dialog>
            )}
          </Grid>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Dialog
        open={open}
        PaperProps={{
          style: {
            width: "1700px",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="lg"
    >
      <Grid className={isMobileRes ? classes.DialogMobile :classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={4}
                  md={4}
                  sm={6}
                  xs={6}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>
                    Set Theme Color
                  </Typography>
                </Grid>
                <Grid item sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Grid
                    container
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ display: "flex" }}
                  >
                    <Grid item>
                      <Button
                        variant="contained"
                        className={classes.addButton}
                        onClick={() => {
                          handleContainerChange(
                            mode === "light" ? Section.Dark : Section.Light
                          );
                          toggleTheme();
                        }}
                      >
                        {mode === "light" ? "Dark" : "light"}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            {renderContent()}
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default ColorPopup;
